'use client';

import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { usePrivy, useWallets } from '@privy-io/react-auth';

import { useAuth, useClobCredentials, usePolymarketClient, useTokenApprovals } from '@/hooks';
import { walletApi } from '@/services/api';
import { clobApi } from '@/services/api/clob.api';
import { useAppDispatch } from '@/store';
import {
  setIsReadyToTrade,
  setProxyError,
  setProxyInitializing,
  setSetupRequested,
  updateUser,
} from '@/store/slices/auth.slice';
import type { TradingWalletKind } from '@/types/auth.types';
import { normalizeClobAddress } from '@/utils/clob.utils';
import {
  clearStoredClobBinding,
  readStoredClobBinding,
} from '@/utils/clobCredentialBindingStorage';
import { deriveSafe } from '@/utils/polymarket.utils';

import { SIGNING_SERVER_URL } from '@/constants/api.constants';
import { MESSAGES } from '@/constants/messages.constants';
import { NATIVE_USDC_CONTRACT_ADDRESS } from '@/constants/tokens.constants';

/**
 * setupStep represents the granular lifecycle of the proxy wallet initialization.
 */
export type ProxySetupStep =
  | 'idle'
  | 'prefetching'
  | 'deploying'
  | 'registering'
  | 'provisioning'
  | 'approving'
  | 'syncing'
  | 'complete'
  | 'error';

/**
 * useProxyInitialization
 *
 * Orchestrates Polymarket trading wallet setup:
 * - Legacy users: Gnosis Safe + POLY_GNOSIS_SAFE CLOB orders
 * - New users (Polymarket): ERC-4337 deposit wallet + POLY_1271 — required when Safe makers are rejected
 */
export function useProxyInitialization() {
  const dispatch = useAppDispatch();
  const [setupStep, setSetupStep] = useState<ProxySetupStep>('idle');

  // TEMP breadcrumb: logs every step transition so we can see the last step
  // reached before a hang on UAT. Remove once proxy setup is confirmed working.
  useEffect(() => {
    console.info('[ProxyInit] step →', setupStep);
  }, [setupStep]);

  const { user, accessToken, isProxyInitializing: isSystemInitializing } = useAuth();
  const { user: privyUser } = usePrivy();
  const { wallets } = useWallets();
  const { client, signer } = usePolymarketClient();
  const { credentials, provisionCredentials, refetchStatus, resetCredentials } =
    useClobCredentials();
  const { ensureAllTokenApprovals, checkAllTokenApprovals } = useTokenApprovals();

  const linkedWallet = wallets.find(
    (wallet) => wallet.address?.toLowerCase() === user?.walletAddress?.toLowerCase(),
  );
  const embeddedWallet = wallets.find((wallet) => wallet.walletClientType === 'privy');
  const eoaAddress =
    user?.walletAddress ||
    linkedWallet?.address ||
    embeddedWallet?.address ||
    privyUser?.wallet?.address;

  const existingProxyAddress = user?.proxyWalletAddress;

  const syncCollateralBalance = async (
    funderAddress: string,
    creds: any,
    kind: TradingWalletKind,
  ) => {
    if (!signer) return;

    setSetupStep('syncing');
    try {
      // Migrated from clob-client-v2. Creating a SecureClient for the funder
      // authenticates the account and establishes the EOA→wallet relationship;
      // balance/allowance is then managed automatically by the SDK during order
      // placement (replacing the explicit updateBalanceAllowance sync). POLY_1271
      // vs Gnosis-Safe is auto-detected from the `wallet` address.
      const { createSecureClient } = await import('@polymarket/client');
      const { signerFrom } = await import('@polymarket/client/viem');

      await createSecureClient({
        signer: signerFrom(signer),
        wallet: funderAddress,
        credentials: {
          key: creds.apiKey,
          secret: creds.secret || creds.apiSecret,
          passphrase: creds.passphrase || creds.apiPassphrase,
        },
      });
    } catch (e: any) {
      if (e?.response?.status === 401) {
        await resetCredentials();
        dispatch(setIsReadyToTrade(false));
      }
    }
  };

  const initializeProxy = useCallback(
    async (clientOverride?: any) => {
      const activeClient = clientOverride || client;

      if (!activeClient || isSystemInitializing || !eoaAddress) {
        return;
      }

      dispatch(setProxyInitializing(true));
      try {
        setSetupStep('prefetching');

        let tradingWalletKind: TradingWalletKind =
          (user?.tradingWalletKind as TradingWalletKind) || 'gnosis_safe';
        let proxyAddress = existingProxyAddress;

        try {
          const info = await walletApi.getProxyInfo();
          if (info?.success && info.data?.proxyAddress) {
            proxyAddress = normalizeClobAddress(info.data.proxyAddress);
            tradingWalletKind = info.data.tradingWalletKind ?? 'gnosis_safe';

            // FIX: If the registered proxy address is the same as the EOA for a deposit_wallet,
            // it's a misconfiguration. We must reset it to force derivation of the correct
            // CREATE2 deposit wallet address.
            if (
              tradingWalletKind === 'deposit_wallet' &&
              proxyAddress?.toLowerCase() === eoaAddress?.toLowerCase()
            ) {
              console.warn(
                '[ProxyInit] Proxy address matches EOA for deposit_wallet. Forcing re-derivation.',
              );
              proxyAddress = undefined;
            }

            dispatch(
              updateUser({
                proxyWalletAddress: proxyAddress,
                tradingWalletKind,
              }),
            );
          }
        } catch (e: any) {
          if (e?.response?.status === 404) {
            tradingWalletKind = 'deposit_wallet';
            proxyAddress = undefined;
            dispatch(updateUser({ tradingWalletKind: 'deposit_wallet' }));
          }
        }

        // Corrupt-data guard: if the backend stored the EOA as the proxy address for a
        // deposit_wallet user (can happen from legacy registration), re-derive the real
        // deposit wallet address and patch both the backend and Redux.
        if (
          tradingWalletKind === 'deposit_wallet' &&
          proxyAddress &&
          proxyAddress.toLowerCase() === eoaAddress?.toLowerCase()
        ) {
          try {
            const derived = await activeClient.deriveDepositWalletAddress();
            proxyAddress = normalizeClobAddress(derived);
            await walletApi.registerProxy({
              proxyAddress,
              deploymentTxHash: 'ALREADY_DEPLOYED',
              status: 'deployed',
              tradingWalletKind,
            });
            dispatch(updateUser({ proxyWalletAddress: proxyAddress, tradingWalletKind }));
          } catch {
            proxyAddress = undefined; // fall through to full deploy below
          }
        }

        let provisionedCreds = credentials;

        if (!provisionedCreds) {
          provisionedCreds = await refetchStatus();
        }

        // Read binding from localStorage directly — Redux may not yet reflect the hydration effect.
        const effectiveBinding =
          user?.clobCredentialBinding ?? (user?.id ? readStoredClobBinding(user.id) : null);

        if (
          tradingWalletKind === 'deposit_wallet' &&
          provisionedCreds &&
          effectiveBinding !== 'deposit_wallet'
        ) {
          await clobApi.deleteCredentials().catch(() => {});
          if (user?.id) clearStoredClobBinding(user.id);
          provisionedCreds = null;
          dispatch(updateUser({ clobCredentials: null as any, clobCredentialBinding: undefined }));
          dispatch(setSetupRequested(true));
        }

        let isActuallyDeployed = false;
        if (proxyAddress) {
          try {
            if (typeof activeClient.getDeployed === 'function') {
              isActuallyDeployed =
                tradingWalletKind === 'deposit_wallet'
                  ? await activeClient.getDeployed(proxyAddress, 'WALLET')
                  : await activeClient.getDeployed(proxyAddress);
            } else {
              const { POLYMARKET_RELAYER_URL } = await import('@/constants/api.constants');
              if (POLYMARKET_RELAYER_URL) {
                const response = await fetch(
                  `${POLYMARKET_RELAYER_URL}/deployed?address=${proxyAddress}`,
                );
                const data = await response.json();
                isActuallyDeployed = !!data.deployed;
              }
            }
          } catch (e) {
            console.warn(
              '[ProxyInit] Could not verify deployment status, assuming not deployed:',
              e,
            );
          }
        }

        let needsDeploy = !proxyAddress || !isActuallyDeployed;
        let needsProvisioning = !provisionedCreds;

        let needsApprovals = false;
        if (proxyAddress && isActuallyDeployed) {
          const status = await checkAllTokenApprovals(proxyAddress);
          needsApprovals = !status.allApproved;
        } else {
          needsApprovals = true;
        }

        if (!needsDeploy && !needsProvisioning && !needsApprovals) {
          dispatch(setIsReadyToTrade(true));
          // For deposit_wallet users: always call updateBalanceAllowance on every login.
          // This is idempotent and ensures the EOA→depositWallet mapping exists in Polymarket's
          // CLOB for users who signed up before this call was added to the provisioning flow.
          if (proxyAddress && tradingWalletKind === 'deposit_wallet' && provisionedCreds) {
            await syncCollateralBalance(proxyAddress, provisionedCreds, tradingWalletKind);
          }
          setSetupStep('idle');
          return proxyAddress;
        }

        let didDeploy = false;

        if (needsDeploy) {
          didDeploy = true;
          setSetupStep('deploying');
          // toast.info(MESSAGES.TOAST.ESTABLISHING_WALLET);

          let deployRes: any;
          let deploymentTxHash = '';

          if (tradingWalletKind === 'deposit_wallet') {
            if (!signer) {
              throw new Error('Wallet signer not ready for deposit wallet deployment.');
            }
            // Migrated from @polymarket/builder-relayer-client to @polymarket/client.
            // createSecureClient derives and — if not already deployed — deploys the
            // signer's deterministic deposit wallet, awaiting the WALLET-CREATE tx
            // and throwing on failure (DeployDepositWalletError). This replaces the
            // manual deriveDepositWalletAddress + deployDepositWallet + retry/wait
            // loop, including the old guard for wait() returning `undefined` on a
            // silent relayer failure. The user's Privy wallet signs; the builder
            // sponsors gas via the BE remote-signing endpoint (remoteBuilderSigning).
            const { createSecureClient, remoteBuilderSigning } = await import('@polymarket/client');
            const { signerFrom } = await import('@polymarket/client/viem');

            const depositSecureClient = await createSecureClient({
              signer: signerFrom(signer),
              apiKey: remoteBuilderSigning({
                url: SIGNING_SERVER_URL,
                headers: () => ({ Authorization: `Bearer ${accessToken}` }),
              }),
            });

            // account.wallet is the (now-deployed) deposit wallet address.
            proxyAddress = normalizeClobAddress(depositSecureClient.account.wallet);
            deploymentTxHash = 'DEPLOYED_VIA_SDK';
          } else {
            try {
              console.info('[ProxyInit] deploy (Safe): requesting signature…');
              deployRes = await activeClient.deploy();
              console.info('[ProxyInit] deploy (Safe): signature returned ✓');
            } catch (error: any) {
              if (
                error.message?.toLowerCase().includes('exists') ||
                error.message?.includes('already deployed')
              ) {
                const safeFactory = activeClient.contractConfig?.SafeContracts?.SafeFactory;
                const safeAddr =
                  proxyAddress || (safeFactory ? deriveSafe(eoaAddress, safeFactory) : undefined);

                if (!safeAddr)
                  throw new Error('Safe already exists but could not determine address.');
                deployRes = { proxyAddress: safeAddr, transactionHash: 'ALREADY_DEPLOYED' };
              } else {
                throw error;
              }
            }

            const result =
              typeof deployRes?.wait === 'function' ? await deployRes.wait() : deployRes;
            proxyAddress = result?.proxyAddress || result?.proxy || result?.address;
            if (proxyAddress) proxyAddress = normalizeClobAddress(proxyAddress);
            deploymentTxHash = result?.transactionHash || 'ALREADY_DEPLOYED';
          }

          if (!proxyAddress) throw new Error('Wallet deployment failed: No address returned.');

          setSetupStep('registering');
          await walletApi.registerProxy({
            proxyAddress,
            deploymentTxHash: deploymentTxHash || 'ALREADY_DEPLOYED',
            status: 'deployed',
            tradingWalletKind,
          });

          dispatch(
            updateUser({
              proxyWalletAddress: proxyAddress,
              tradingWalletKind,
            }),
          );
          needsApprovals = true;
          needsProvisioning = true;
        }

        if (needsApprovals && proxyAddress) {
          setSetupStep('approving');
          if (didDeploy) await new Promise((r) => setTimeout(r, 2000));

          const allOk = await ensureAllTokenApprovals(
            proxyAddress,
            activeClient,
            tradingWalletKind,
          );
          if (!allOk) throw new Error('Token approvals were rejected or failed.');
        }

        if (needsProvisioning || !provisionedCreds) {
          setSetupStep('provisioning');
          if (didDeploy) await new Promise((r) => setTimeout(r, 1000));

          provisionedCreds = await provisionCredentials(proxyAddress, { tradingWalletKind });
          if (!provisionedCreds) throw new Error('Failed to provision trading credentials.');
        }

        dispatch(setIsReadyToTrade(true));

        if (proxyAddress && provisionedCreds) {
          await syncCollateralBalance(proxyAddress, provisionedCreds, tradingWalletKind);
        }

        setSetupStep('complete');
        toast.success(MESSAGES.TOAST.TRADING_ACCOUNT_INITIALIZED);
        return proxyAddress;
      } catch (error: any) {
        if (process.env.NODE_ENV === 'development') {
          console.error('[ProxyInit] Pipeline failed:', error);
        }
        setSetupStep('error');
        // The relayer client (ky under the hood) throws its own raw
        // "TimeoutError: Request timed out: GET https://relayer-v2..." when
        // Polymarket's relayer is slow/unreachable — e.g. the initial
        // getDeployed() check below swallows this into a console.warn, but
        // deploy() re-checks deployment status internally and that call
        // isn't guarded, so its timeout reaches here unfiltered. Surface a
        // friendly, actionable message instead of a raw URL to the user.
        const isNetworkTimeout =
          error?.name === 'TimeoutError' ||
          /^Request (timed out|failed):/i.test(error?.message || '');
        const msg = isNetworkTimeout
          ? MESSAGES.TOAST.NETWORK_TIMEOUT_RETRY
          : error.message || 'Trading initialization failed.';
        dispatch(setProxyError(msg));
        toast.error(msg);
        throw error;
      } finally {
        dispatch(setProxyInitializing(false));
      }
    },
    [
      client,
      isSystemInitializing,
      eoaAddress,
      existingProxyAddress,
      user?.tradingWalletKind,
      user?.clobCredentialBinding,
      credentials,
      signer,
      dispatch,
      provisionCredentials,
      refetchStatus,
      checkAllTokenApprovals,
      ensureAllTokenApprovals,
    ],
  );

  const syncBalance = useCallback(async () => {
    const kind = (user?.tradingWalletKind as TradingWalletKind) || 'gnosis_safe';
    if (!existingProxyAddress || !credentials || !signer) return;
    await syncCollateralBalance(existingProxyAddress, credentials, kind);
  }, [existingProxyAddress, credentials, signer, user?.tradingWalletKind]);

  return {
    initializeProxy,
    syncBalance,
    isDeploying: isSystemInitializing,
    setupStep,
    setSetupStep,
  };
}
